import type { ReactNode } from 'react'
import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { THEMES } from '../constants'
import { GameStatus } from '../generated/graphql'
import { changeLanguage, type SupportedLanguage } from '../i18n'

export interface DashboardFilters {
  gameStatus?: {
    timeline?: GameStatus[]
    gamesList?: GameStatus[]
  }
  year?: {
    timeline?: Date | null
    gamesList?: Date | null
  }
  search?: {
    gamesList?: string
  }
  favouritesOnly?: {
    gamesList?: boolean
  }
}

export interface Settings {
  compact?: boolean
  direction: 'ltr' | 'rtl'
  responsiveFontSizes?: boolean
  roundedCorners?: boolean
  theme?: string
  dashboardFilters?: DashboardFilters
  favourites?: string[]
  language?: SupportedLanguage
}

export interface SettingsContextValue {
  settings: Settings
  saveSettings: (update: Settings) => void
  updateDashboardFilter: (page: string, filterType: string, value: any) => void
  setLanguage: (language: SupportedLanguage) => void
}

interface SettingsProviderProps {
  children?: ReactNode
}

const initialSettings: Settings = {
  compact: true,
  direction: 'ltr',
  responsiveFontSizes: true,
  roundedCorners: false,
  theme: THEMES.MAIN,
  favourites: [],
  language: 'en',
  dashboardFilters: {
    gameStatus: {
      timeline: [GameStatus.Draft, GameStatus.Anticipation, GameStatus.Live],
      gamesList: [GameStatus.Draft, GameStatus.Anticipation, GameStatus.Live],
    },
    year: {
      timeline: null,
      gamesList: null,
    },
    search: {
      gamesList: '',
    },
    favouritesOnly: {
      gamesList: false,
    },
  },
}

export const restoreSettings = (): Settings | null => {
  let settings: Settings | null = null
  let storedData: string | null = null

  try {
    storedData = window.localStorage.getItem('settings')

    if (storedData) {
      const parsedSettings = JSON.parse(storedData)

      // Helper function to deserialize Date strings back to Date objects
      const deserializeDates = (yearFilters: any) => {
        if (!yearFilters) return yearFilters
        const result: any = {}
        for (const [key, value] of Object.entries(yearFilters)) {
          if (value && typeof value === 'string') {
            const date = new Date(value)
            result[key] = isNaN(date.getTime()) ? null : date
          } else {
            result[key] = value
          }
        }
        return result
      }

      // Merge with defaults to ensure all properties exist, especially new ones
      settings = {
        ...initialSettings,
        ...parsedSettings,
        favourites: parsedSettings.favourites || [],
        language: parsedSettings.language || initialSettings.language,
        dashboardFilters: {
          ...initialSettings.dashboardFilters,
          ...parsedSettings.dashboardFilters,
          gameStatus: {
            ...initialSettings.dashboardFilters?.gameStatus,
            ...parsedSettings.dashboardFilters?.gameStatus,
          },
          year: deserializeDates({
            ...initialSettings.dashboardFilters?.year,
            ...parsedSettings.dashboardFilters?.year,
          }),
          search: {
            ...initialSettings.dashboardFilters?.search,
            ...parsedSettings.dashboardFilters?.search,
          },
        },
      }
    } else {
      settings = initialSettings
    }
  } catch (err) {
    console.error('Error restoring settings:', err, { storedData })
    // If stored data is not a stringified JSON this will fail,
    // that's why we catch the error and fall back to defaults
    settings = initialSettings
  }

  return settings
}

export const storeSettings = (settings: Settings): void => {
  window.localStorage.setItem('settings', JSON.stringify(settings))
}

const SettingsContext = createContext<SettingsContextValue>({
  settings: initialSettings,
  saveSettings: () => undefined,
  updateDashboardFilter: () => undefined,
  setLanguage: () => undefined,
})

export const SettingsProvider = (props: SettingsProviderProps) => {
  const { children } = props

  // Initialize state with restored settings immediately to avoid race condition
  const initialStateValue = useMemo(() => {
    const restored = restoreSettings()
    return restored || initialSettings
  }, [])

  const [settings, setSettings] = useState<Settings>(initialStateValue)

  useEffect(() => {
    if (initialStateValue.language) {
      changeLanguage(initialStateValue.language)
    }
  }, [initialStateValue.language])

  const saveSettings = (updatedSettings: Settings): void => {
    // Merge with existing settings to prevent data loss
    const mergedSettings = {
      ...settings,
      ...updatedSettings,
      favourites: updatedSettings.favourites || settings.favourites,
      dashboardFilters: {
        ...settings.dashboardFilters,
        ...updatedSettings.dashboardFilters,
      },
    }

    setSettings(mergedSettings)
    storeSettings(mergedSettings)
  }

  const updateDashboardFilter = (
    page: string,
    filterType: string,
    value: any
  ): void => {
    const updatedSettings = {
      ...settings,
      dashboardFilters: {
        ...settings.dashboardFilters,
        [filterType]: {
          ...settings.dashboardFilters?.[filterType as keyof DashboardFilters],
          [page]: value,
        },
      },
    }
    setSettings(updatedSettings)
    storeSettings(updatedSettings)
  }

  const setLanguageHandler = useCallback(
    (language: SupportedLanguage): void => {
      changeLanguage(language)
      const updatedSettings = {
        ...settings,
        language,
      }
      setSettings(updatedSettings)
      storeSettings(updatedSettings)
    },
    [settings]
  )

  return (
    <SettingsContext.Provider
      value={{
        settings,
        saveSettings,
        updateDashboardFilter,
        setLanguage: setLanguageHandler,
      }}
    >
      {children}
    </SettingsContext.Provider>
  )
}

export const SettingsConsumer = SettingsContext.Consumer

export default SettingsContext
